import React, { useRef, useState, useEffect, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FaUserCircle } from 'react-icons/fa';
import logo from '../../../../img/logo-prefeitura/logomarca-oficial.png';
import jsPDF from "jspdf";
import logoBase64 from "./logoBase64";
import { fetchPhotoAsBase64, getDisplayUrls, useStudentPhoto } from '../../../../utils/photoLoader';

// Estilo global para impressão
const GlobalStyle = createGlobalStyle`
@media print {
    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      line-height: 1.4;
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      color: black; 
    }
    .img {
      max-width: 100%;
    }
    button {
      display: none;
    }
    #contract {
      position: relative;
      width: 100%;
      min-height: 100vh; 
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      display: flex; 
      flex-direction: column; 
      color: black; 
    }
    #contract > .container { 
        flex: 1; 
    }
    .contract-student-photo {
      width: 60px !important;
      height: 60px !important;
      border: 1px solid #000 !important;
    }
    @page {
      size: A4;
      margin: 20mm;
    }
  }
`;

// Componentes estilizados (CSS-in-JS)
const Contract = styled.div`
  font-family: 'Times New Roman', Times, serif;
  font-size: 10pt;
  
  line-height: 1.2;
  box-sizing: border-box;  
  width: 100%; 
  color: black; 
  
  @media print {
    margin: 0;
    padding: 0;
  }
`;

const Container = styled.div`
  max-width: 210mm;
  margin: 0 auto;
  color: black; 

  @media print {
  width: 100%;
  height: 100%;
  }
`;

const ContractHeader = styled.div`
  text-align: center;
  margin-bottom: 10px;
  color: black; 
`;

const HeaderImg = styled.img`
  max-width: 150px;
  height: auto;
  margin-bottom: 5px;

  @media print {
    max-width: 300px;
  }
`;

const Title = styled.h1`
  font-size: 14pt;
  margin-top: 5px;
  color: black; 
`;

const Subtitle = styled.h2`
  font-size: 12pt;
  margin-top: 3px;
  color: black; 
`;

const Section = styled.div`
  margin-bottom: 18px;
  color: var(--text-color);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 12px;
`;

const Label = styled.span`
  font-weight: bold;
  color: var(--primary-color);
`;

const InfoInline = styled.div`
  margin-bottom: 3px;
  color: black; 
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
  color: black; 
`;

const Address = styled.div`
  grid-column: span 2;
  color: black; 
`;


const ResponsibleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 10pt;
  color: var(--text-color);
`;

const ResponsibleTh = styled.th`
  border-bottom: 2px solid var(--primary-color);
  padding: 6px 4px;
  text-align: left;
  background: none;
  color: var(--primary-color);
  font-size: 10pt;
`;

const ResponsibleTd = styled.td`
  border-bottom: 1px solid var(--border-color);
  padding: 6px 4px;
  text-align: left;
  color: var(--text-color);
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 8pt;
  color: black; 
`;

const StudentPhotoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  
  @media print {
    margin-bottom: 10px;
  }
`;

const StudentPhoto = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid var(--border-color);
  
  @media print {
    width: 60px;
    height: 60px;
    border: 1px solid #ddd;
  }
`;

const StudentPhotoIcon = styled(FaUserCircle)`
  width: 80px;
  height: 80px;
  color: var(--secondary-color);
  
  @media print {
    width: 60px;
    height: 60px;
    color: #999;
  }
`;

const StudentInfoWithPhoto = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PrintButton = styled.button`
  float: right;
  margin: 10px;
  background-color: var(--primary-color);
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 5px;

  &:hover {
    background-color: var(--hover-color);
  }
`;

// Função para formatar o número de telefone
const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  phone = phone.replace(/\D/g, "");
  if (phone.length === 11) {
    return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
  } else if (phone.length === 10) {
    return `(${phone.slice(0, 2)}) ${phone.slice(2, 6)}-${phone.slice(6)}`;
  }
  return phone;
};

// Função para formatar data corretamente (evita problemas de fuso horário)
const formatBirthDate = (dateString) => {
  if (!dateString) return 'Não Informado';
  
  // Se a data vier no formato ISO (YYYY-MM-DD), criar a data sem problemas de fuso horário
  if (dateString.includes('-')) {
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('pt-BR');
  }
  
  // Fallback para outros formatos
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

// Componente de foto do aluno.
// Usa o base64 vindo do photoLoader (mesma fonte usada na geração do PDF) e, se a
// conversão falhar, cai para a URL direta apenas para exibição.
const PhotoWithFallback = ({ photoUrl, alt, className, onError, onLoad, style }) => {
  const { src, isLoading, hasError } = useStudentPhoto(photoUrl);
  const [directUrlIndex, setDirectUrlIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  const directUrls = useMemo(() => getDisplayUrls(photoUrl), [photoUrl]);

  useEffect(() => {
    setDirectUrlIndex(0);
    setFailed(false);
  }, [photoUrl]);

  if (!photoUrl || failed) return null;

  // Enquanto converte, não renderiza nada (evita piscar a imagem sem CORS)
  if (isLoading) return null;

  const resolvedSrc = src || (hasError ? directUrls[directUrlIndex] : null);
  if (!resolvedSrc) return null;

  const handleError = () => {
    if (!src && directUrlIndex < directUrls.length - 1) {
      setDirectUrlIndex((prev) => prev + 1);
      return;
    }
    console.log('❌ Não foi possível exibir a foto do aluno:', photoUrl);
    setFailed(true);
    if (onError) onError();
  };

  return (
    <StudentPhoto
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={style}
      onLoad={() => onLoad && onLoad()}
      onError={handleError}
      referrerPolicy="no-referrer"
    />
  );
};

/**
 * Componente ContractView
 * Renderiza e permite impressão do contrato do aluno
 */
const ContractView = ({ student, polo }) => {
  const contractRef = useRef();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPrintButton, setShowPrintButton] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Verificação de dados necessários
  if (!student || !polo) {
    return <div>Erro: Dados do estudante ou polo não encontrados.</div>;
  }

  // Verifica se algum responsável tem parentesco informado
  const hasRelation = student?.responsibles && student.responsibles.some(r => r.relation && r.relation !== 'Não Informado');

  // Monta endereço apenas com campos existentes e não vazios e diferentes de 'Não Informado'
  const addressParts = [];
  if (student?.street && student.street !== 'Não Informado') addressParts.push(student.street);
  if (student?.number && student.number !== 'Não Informado') addressParts.push(student.number);
  if (student?.neighborhood && student.neighborhood !== 'Não Informado') addressParts.push(student.neighborhood);
  if (student?.city && student.city !== 'Não Informado') addressParts.push(student.city);
  if (student?.state && student.state !== 'Não Informado') addressParts.push(student.state);
  if (student?.zip_code && student.zip_code !== 'Não Informado') addressParts.push(student.zip_code);
  const addressString = addressParts.join(', ');

  const handlePrint = async () => {
    setShowPrintButton(false);
    setIsGeneratingPdf(true);
    
    if (!student || !polo) {
      setIsGeneratingPdf(false);
      return;
    }
    
    // Busca a foto pela URL do aluno (com cache) — nunca a partir do que está na tela,
    // para não correr o risco de capturar a foto de outro aluno da listagem.
    const photoDataUrl = student?.photo_url ? await fetchPhotoAsBase64(student.photo_url) : null;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = 22;
    const pageWidth = 210;
    
    // Logo centralizada
    const logoWidth = 50;
    const logoHeight = 15;
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(logoBase64, 'PNG', logoX, y, logoWidth, logoHeight);
    y += logoHeight + 12;
    
    // Título principal
    doc.setFont('times', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(48, 65, 98); // --primary-color
    doc.text("Termo de Matrícula - Atividades Culturais", pageWidth / 2, y, { align: 'center' });
    y += 8;
    doc.setFont('times', 'italic');
    doc.setFontSize(12);
    doc.setTextColor(108, 117, 125); // --secondary-color
    if (polo && polo.name) {
      const poloNameWithYear = polo.name.toLowerCase().includes('são domingos') ? `${polo.name} - 2026` : polo.name;
      doc.text(poloNameWithYear, pageWidth / 2, y, { align: 'center' });
      y += 8;
    } else {
      y += 4;
    }
    
    // Linha divisória minimalista
    doc.setDrawColor(206, 212, 218); // --border-color
    doc.setLineWidth(0.4);
    doc.line(25, y, 185, y);
    y += 7;
    
    // Dados do aluno com foto
    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(48, 65, 98);
    doc.text("Dados do(a) Aluno(a):", 25, y);
    y += 7;
    
    // Adicionar foto do aluno se existir
    if (photoDataUrl) {
      try {
        const photoSize = 25; // 25mm
        doc.addImage(photoDataUrl, 'JPEG', 160, y - 2, photoSize, photoSize);
      } catch (error) {
        console.log('✗ Não foi possível adicionar a foto ao PDF:', error.message);
      }
    } else if (student?.photo_url) {
      console.log('✗ Foto do aluno indisponível, gerando o contrato sem ela');
    }

    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(33, 37, 41); // --text-color
    doc.text(`Nome: ${student?.first_name || ''} ${student?.last_name || ''}`, 25, y);
    doc.text(`Gênero: ${student?.gender === 'male' ? 'Masculino' : student?.gender === 'female' ? 'Feminino' : 'Não Informado'}`, 25, y + 5.5);
    y += 11;
    doc.text(`Nascimento: ${formatBirthDate(student?.birthdate)}`, 25, y);
    doc.text(`Telefone: ${formatPhoneNumber(student?.phone_number || '')}`, 25, y + 5.5);
    y += 11;
    doc.text(`Pai: ${student?.father_name || 'Não Informado'}`, 25, y);
    doc.text(`Mãe: ${student?.mother_name || 'Não Informado'}`, 25, y + 5.5);
    y += 11; // Espaço extra para acomodar a foto
    doc.setFont('times', 'italic');
    doc.setTextColor(0, 0, 0); // cor preta
    doc.text(addressString ? `Endereço: ${addressString}` : '', 25, y, { maxWidth: 160 });
    doc.setFont('times', 'normal');
    doc.setTextColor(33, 37, 41);
    y += 10;
    // Responsáveis minimalista
    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(48, 65, 98);
    doc.text("Responsáveis:", 25, y);
    y += 6;
    doc.setFont('times', 'bold');
    doc.setFontSize(10);
    doc.text("Nome", 25, y);
    doc.text("Parentesco", 90, y);
    doc.text("Telefone", 140, y);
    y += 4;
    doc.setFont('times', 'normal');
    if (student?.responsibles && student.responsibles.length > 0) {
      student.responsibles.forEach((responsible) => {
        doc.text(`${responsible?.first_name || ''} ${responsible?.last_name || ''}`, 25, y);
        doc.text(`${responsible?.relation || 'Não Informado'}`, 90, y);
        doc.text(`${formatPhoneNumber(responsible?.phone_number || '')}`, 140, y);
        y += 5.5;
      });
    } else {
      doc.text("Nenhum responsável cadastrado.", 25, y);
      y += 5.5;
    }
    y += 5;
    // Linha divisória minimalista
    doc.setDrawColor(206, 212, 218);
    doc.setLineWidth(0.4);
    doc.line(25, y, 185, y);
    y += 7;
    // Oficinas
    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(48, 65, 98);
    doc.text("Oficinas Cadastradas:", 25, y);
    y += 7;
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(33, 37, 41);
    const oficinasAluno = (student.categories || []).map(course => course.name);
    if (oficinasAluno.length > 0) {
      doc.text(oficinasAluno.join(', '), 28, y + 3, { maxWidth: 160 });
      y += 8;
    } else {
      doc.text('Nenhuma oficina cadastrada', 28, y + 3);
      y += 8;
    }
    y += 4;
    // Linha divisória minimalista
    doc.setDrawColor(206, 212, 218);
    doc.setLineWidth(0.4);
    doc.line(25, y, 185, y);
    y += 7;
    // Termos
    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(48, 65, 98);
    doc.text("Termos e Condições:", 25, y);
    y += 7;
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(33, 37, 41);
    const termos = [
      "1. As aulas terão duração de 1 hora, duas vezes por semana, conforme horário estabelecido.",
      "2. Não haverá reposição de aulas em caso de falta.",
      "3. É obrigatória a assiduidade e o porte do material necessário.",
      "4. Três faltas sem justificativa resultarão em advertência aos pais.",
      "5. Pais/responsáveis são responsáveis pela guarda e transporte dos alunos.",
      "6. Para oficinas de teatro, bateria, violão, teclado, fanfarra e flauta, é necessário saber ler.",
      "7. Este termo de matrícula é válido mediante assinatura do responsável."
    ];
    termos.forEach((t) => {
      doc.text(t, 28, y, { maxWidth: 160 });
      y += 5.5;
      if (y > 270) { doc.addPage(); y = 20; }
    });
    y += 4;
    // Linha divisória minimalista
    doc.setDrawColor(206, 212, 218);
    doc.setLineWidth(0.4);
    doc.line(25, y, 185, y);
    y += 7;
    // Autorização de uso de imagem
    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(48, 65, 98);
    doc.text("Autorização de Uso de Imagem:", 25, y);
    y += 7;
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(33, 37, 41);
    doc.text(
      "Eu, o(a) responsável pelo(a) aluno(a) acima identificado(a), autorizo o uso da imagem do(a) mesmo(a) para fins de divulgação das atividades culturais promovidas pela Secretaria de Cultura, incluindo fotos e vídeos, em materiais impressos, digitais e redes sociais.",
      28, y, { maxWidth: 160 }
    );
    y += 18; // aumenta o espaço antes da assinatura
    doc.setDrawColor(0, 0, 0); // linha preta
    doc.setLineWidth(0.4);
    doc.line(80, y, 150, y);
    y += 8;
    doc.setFont('times', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0); // cor preta
    doc.text("Assinatura do(a) Responsável", 115, y, { align: 'center' });
    // Data atual no rodapé
    const dataAtual = new Date();
    const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
    const dataFormatada = `Sorriso, ${dataAtual.getDate()} de ${meses[dataAtual.getMonth()]} de ${dataAtual.getFullYear()}`;
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(108, 117, 125);
    doc.text(dataFormatada, pageWidth - 20, 292, { align: 'right' });
    // Gerar blob e url
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);
    setIsGeneratingPdf(false);
    console.log('=== PDF GERADO COM SUCESSO ===');
  };

  return (
    <>
      {showPrintButton && (
        <PrintButton onClick={handlePrint} disabled={isGeneratingPdf}>
          {isGeneratingPdf ? 'Gerando PDF...' : 'Imprimir'}
        </PrintButton>
      )}
      <GlobalStyle />
      {pdfUrl && (
        <div style={{ width: '100%', height: '90vh', marginTop: 20 }}>
          <iframe
            src={pdfUrl}
            title="Contrato PDF"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      )}
      {showPrintButton && (
        <Contract id="contract" ref={contractRef}>
          <Container>
            <ContractHeader>
              <HeaderImg src={logo} alt="Logo" />
              <Title>Termo de Matrícula - Atividades Culturais</Title>
              {polo && <Subtitle>{polo.name.toLowerCase().includes('são domingos') ? `${polo.name} - 2026` : polo.name}</Subtitle>}
            </ContractHeader>

            <Section>
              <Title>Dados do(a) Aluno(a)</Title>
              <StudentPhotoContainer>
                {student?.photo_url ? (
                  <PhotoWithFallback
                    photoUrl={student.photo_url}
                    alt={`Foto de ${student?.first_name || ''} ${student?.last_name || ''}`}
                    className="contract-student-photo"
                    onError={() => {
                      console.log('❌ Erro ao carregar foto do aluno no contrato');
                    }}
                    onLoad={() => {
                      console.log('✅ Foto carregada com sucesso no contrato');
                    }}
                  />
                ) : (
                  <StudentPhotoIcon />
                )}
                <StudentInfoWithPhoto>
                  <InfoGrid>
                    <InfoInline><Label>Nome:</Label> {student?.first_name || ''} {student?.last_name || ''}</InfoInline>
                    <InfoInline><Label>Nascimento:</Label> {formatBirthDate(student?.birthdate)}</InfoInline>
                    <InfoInline><Label>Gênero:</Label> {student?.gender === 'male' ? 'Masculino' : student?.gender === 'female' ? 'Feminino' : 'Não Informado'}</InfoInline>
                    <InfoInline><Label>Telefone:</Label> {formatPhoneNumber(student?.phone_number || '')}</InfoInline>
                    <InfoInline><Label>Pai:</Label> {student?.father_name || 'Não Informado'}</InfoInline>
                    <InfoInline><Label>Mãe:</Label> {student?.mother_name || 'Não Informado'}</InfoInline>
                    {addressString && (
                      <Address><Label>Endereço:</Label> {addressString}</Address>
                    )}
                  </InfoGrid>
                </StudentInfoWithPhoto>
              </StudentPhotoContainer>
              {student?.responsibles && student.responsibles.length > 0 ? (
                <>
                  <Label>Responsáveis:</Label>
                  <ResponsibleTable>
                    <thead>
                      <tr>
                        <ResponsibleTh>Nome</ResponsibleTh>
                        {hasRelation && <ResponsibleTh>Parentesco</ResponsibleTh>}
                        <ResponsibleTh>Telefone</ResponsibleTh>
                      </tr>
                    </thead>
                    <tbody>
                      {(student?.responsibles || []).map((responsible) => (
                        <tr key={responsible?.id || Math.random()}>
                          <ResponsibleTd>{responsible?.first_name || ''} {responsible?.last_name || ''}</ResponsibleTd>
                          {hasRelation && <ResponsibleTd>{responsible?.relation || ''}</ResponsibleTd>}
                          <ResponsibleTd>{formatPhoneNumber(responsible?.phone_number || '')}</ResponsibleTd>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsibleTable>
                </>
              ) : (
                <Label>Responsável: Nenhum responsável cadastrado.</Label>
              )}
            </Section>

            <Section>
              <Title>Oficinas Cadastradas</Title>
              <div style={{ marginTop: 8, marginBottom: 8 }}>
                {student.categories && student.categories.length > 0 ? (
                  <span style={{ color: 'var(--primary-color)', fontWeight: 500 }}>
                    {student.categories.map(course => course.name).join(', ')}
                  </span>
                ) : (
                  <span style={{ color: 'var(--secondary-color)' }}>Nenhuma oficina cadastrada</span>
                )}
              </div>
            </Section>

            <Section>
              <Title>Termos e Condições</Title>
              <div>
                1. As aulas terão duração de 1 hora, duas vezes por semana, conforme horário estabelecido. <br />
                2. Não haverá reposição de aulas em caso de falta. <br />
                3. É obrigatória a assiduidade e o porte do material necessário. <br />
                4. Três faltas sem justificativa resultarão em advertência aos pais. <br />
                5. Pais/responsáveis são responsáveis pela guarda e transporte dos alunos. <br />
                6. Para oficinas de teatro, bateria, violão, teclado, fanfarra e flauta, é necessário saber ler. <br />
              </div>
              <Title>Autorização de Uso de Imagem</Title>
              <div>
                Eu, o(a) responsável pelo(a) aluno(a) acima identificado(a), autorizo o uso da imagem do(a) mesmo(a) para fins de divulgação das atividades culturais promovidas pela Secretaria de Cultura, incluindo fotos e vídeos, em materiais impressos, digitais e redes sociais.
              </div>
              <div>
                7. Este termo de matrícula é válido mediante assinatura do responsável.
              </div>
            </Section>

            <Footer>
              <p style={{ color: 'black', marginTop: '40px' }}>
                _________________________________________<br />
                Assinatura do(a) Responsável
              </p>
            </Footer>
          </Container>
        </Contract>
      )}
    </>
  );
};

export default ContractView;